// src/views/admin/users/customers/view/tabs/OrdersTab.jsx

import React, { useEffect, useState } from 'react';
import { useDispatch }       from 'react-redux';
import { RiShoppingBagLine } from 'react-icons/ri';
import Card                  from '@/components/ui/cards/Card';
import { getOrderService }   from '@/services/OrderServices';

export default function OrdersTab({ customerId }) {
  const dispatch = useDispatch();
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(getOrderService({ userId: customerId }))
      .then(res => {
        // API returns { data: { data: [ ...orders ] } }
        setOrders(res.data?.data?.data || []);
      })
      .finally(() => setLoading(false));
  }, [customerId, dispatch]);

  return (
    <Card className="rounded-xl border border-gray-200 bg-white px-6 py-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <RiShoppingBagLine className="text-2xl text-slate-700" />
        <h3 className="text-2xl font-semibold">Orders</h3>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading orders…</div>
      ) : orders.length === 0 ? (
        <div className="text-center text-gray-500 py-12">No orders found.</div>
      ) : (
        <ul className="space-y-6">
          {orders.map((o, idx) => {
            const dateStr = new Date(o.order_datetime).toLocaleDateString();
            const totalStr = Number(o.total).toFixed(2);
            const names = (o.order_items || [])
              .map(it => it.itemName || it.item_id?.name)
              .filter(Boolean)
              .join(', ');

            return (
              <li
                key={idx}
                className="border rounded-lg p-5 hover:shadow-lg transition-shadow"
              >
                {/* Order summary bar */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                  <div className="font-medium text-lg text-slate-800">
                    {names || '— no items —'}
                  </div>
                  <div className="text-sm text-gray-500 mt-2 sm:mt-0">
                    {dateStr}
                  </div>
                  <div className="text-lg font-semibold text-slate-900 mt-2 sm:mt-0">
                    Total: ${totalStr}
                  </div>
                </div>

                {/* Detailed items */}
                <ul className="space-y-3">
                  {o.order_items?.map((it, i) => {
                    const unitName  = it.itemName || it.item_id?.name || 'Unknown';
                    const unitPrice = Number(it.price ?? 0);
                    const qty       = Number(it.quantity ?? 0);
                    const lineTotal = (unitPrice * qty).toFixed(2);
                    // derive image URL; adjust path as needed
                    const imgSrc = it.item_id?.file_name
                      ? `/uploads/product/${it.item_id.file_name}`
                      : 'https://via.placeholder.com/40';

                    return (
                      <li key={i} className="flex items-center">
                        <img
                          src={imgSrc}
                          alt={unitName}
                          className="w-10 h-10 rounded object-cover mr-4 flex-shrink-0"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-slate-800">{unitName}</div>
                          <div className="text-sm text-gray-500">
                            {qty} × ${unitPrice.toFixed(2)}
                          </div>
                        </div>
                        <div className="font-medium text-slate-900">
                          ${lineTotal}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
